import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Star,
  MessageSquare,
  Pencil,
  Trash2,
  BookOpen,
  Award,
  Route,
  Calendar,
  ThumbsUp,
  MessageSquareOff,
} from 'lucide-react';
import { toast } from '@/components/ui/Toast';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import TooltipArrow from '@/components/ui/TooltipArrow';
import ReviewModal from '@/components/reviews/ReviewModal';
import { reviewService } from '@/services/review.service';
import { ReviewType, type Review } from '@/types/review.types';

type FilterType = 'all' | ReviewType;

const FILTER_OPTIONS: { value: FilterType; label: string; icon: React.ElementType }[] = [
  { value: 'all', label: 'All Reviews', icon: MessageSquare },
  { value: ReviewType.COURSE, label: 'Courses', icon: BookOpen },
  { value: ReviewType.CERTIFICATE, label: 'Certifications', icon: Award },
  { value: ReviewType.LEARNING_PATH, label: 'Learning Paths', icon: Route },
];

// Helper to get entity link
const getEntityLink = (review: Review): string => {
  if (review.course_id) return `/learn/course/${review.course_id}`;
  if (review.certification_id) return `/learn/certification/${review.certification_id}`;
  if (review.learning_path_id) return `/learn/learning-path/${review.learning_path_id}`;
  return '#';
};

// Helper to get entity type label
const getEntityTypeLabel = (review: Review): string => {
  if (review.review_type === ReviewType.COURSE) return 'Course';
  if (review.review_type === ReviewType.CERTIFICATE) return 'Certification';
  if (review.review_type === ReviewType.LEARNING_PATH) return 'Learning Path';
  return 'Unknown';
};

// Helper to get entity type icon
const getEntityTypeIcon = (review: Review): React.ElementType => {
  if (review.review_type === ReviewType.COURSE) return BookOpen;
  if (review.review_type === ReviewType.CERTIFICATE) return Award;
  if (review.review_type === ReviewType.LEARNING_PATH) return Route;
  return MessageSquare;
};

// Format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default function MyReviews() {
  const queryClient = useQueryClient();
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [reviewToEdit, setReviewToEdit] = useState<Review | null>(null);

  // Fetch user's reviews
  const { data: reviewsData, isLoading, refetch } = useQuery({
    queryKey: ['my-reviews'],
    queryFn: () => reviewService.getMyReviews(),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (reviewId: string) => reviewService.deleteReview(reviewId),
    onSuccess: () => {
      toast.success('Review deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['my-reviews'] });
      setShowDeleteModal(false);
      setReviewToDelete(null);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete review');
    },
  });

  // Filter reviews
  const filteredReviews = (reviewsData?.reviews || []).filter((review) => {
    if (filterType === 'all') return true;
    return review.review_type === filterType;
  });

  // Handlers
  const handleEditClick = (review: Review) => {
    setReviewToEdit(review);
    setShowEditModal(true);
  };

  const handleDeleteClick = (reviewId: string) => {
    setReviewToDelete(reviewId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (reviewToDelete) {
      deleteMutation.mutate(reviewToDelete);
    }
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setReviewToEdit(null);
  };

  const handleEditSuccess = () => {
    refetch();
  };

  // Stats
  const totalReviews = reviewsData?.reviews?.length || 0;
  const courseReviews = reviewsData?.reviews?.filter((r) => r.review_type === ReviewType.COURSE).length || 0;
  const certReviews = reviewsData?.reviews?.filter((r) => r.review_type === ReviewType.CERTIFICATE).length || 0;
  const pathReviews = reviewsData?.reviews?.filter((r) => r.review_type === ReviewType.LEARNING_PATH).length || 0;
  const avgRating = totalReviews > 0
    ? (reviewsData?.reviews?.reduce((acc, r) => acc + r.rating, 0) || 0) / totalReviews
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <Star className="w-7 h-7" />
              My Reviews
            </h1>
            <p className="text-amber-100">
              Manage your reviews and ratings across all courses and certifications
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-amber-100 text-xs">Total Reviews</p>
            <p className="text-2xl font-bold">{totalReviews}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-amber-100 text-xs">Avg Rating</p>
            <p className="text-2xl font-bold flex items-center gap-1">
              {avgRating > 0 ? avgRating.toFixed(1) : '-'}
              <Star className="w-5 h-5 fill-white" />
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-amber-100 text-xs">Courses</p>
            <p className="text-2xl font-bold">{courseReviews}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-amber-100 text-xs">Certifications</p>
            <p className="text-2xl font-bold">{certReviews}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-amber-100 text-xs">Learning Paths</p>
            <p className="text-2xl font-bold">{pathReviews}</p>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex overflow-x-auto">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => setFilterType(option.value)}
                className={`flex items-center gap-2 px-6 py-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  filterType === option.value
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <option.icon className="w-4 h-4" />
                {option.label}
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                  {option.value === 'all'
                    ? totalReviews
                    : option.value === ReviewType.COURSE
                    ? courseReviews
                    : option.value === ReviewType.CERTIFICATE
                    ? certReviews
                    : pathReviews}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            // Loading State
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                    <div className="flex-1">
                      <div className="h-5 bg-gray-200 rounded w-48 mb-2" />
                      <div className="h-4 bg-gray-200 rounded w-32 mb-3" />
                      <div className="h-3 bg-gray-200 rounded w-full mb-2" />
                      <div className="h-3 bg-gray-200 rounded w-3/4" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredReviews.length === 0 ? (
            // Empty State
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquareOff size={28} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No reviews yet</h3>
              <p className="text-sm text-gray-500 mb-6">
                {filterType === 'all'
                  ? "You haven't written any reviews yet. Start by rating courses you've completed!"
                  : `You haven't reviewed any ${filterType === ReviewType.COURSE ? 'courses' : filterType === ReviewType.CERTIFICATE ? 'certifications' : 'learning paths'} yet.`}
              </p>
              <Link
                to="/learn"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white text-sm font-medium rounded-xl hover:bg-amber-600 transition-colors"
              >
                <BookOpen size={16} />
                Browse Courses
              </Link>
            </div>
          ) : (
            // Reviews List
            <div className="space-y-4">
              {filteredReviews.map((review) => {
                const EntityIcon = getEntityTypeIcon(review);
                return (
                  <div
                    key={review._id}
                    className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      {/* Entity Icon */}
                      <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <EntityIcon className="w-6 h-6 text-amber-600" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div>
                            <TooltipArrow content={review.entity_title || ''} position="top" delay={200} block>
                              <Link
                                to={getEntityLink(review)}
                                className="font-semibold text-gray-900 hover:text-amber-600 transition-colors block truncate"
                              >
                                {review.entity_title || 'Untitled'}
                              </Link>
                            </TooltipArrow>
                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                              <span className="px-2 py-0.5 bg-gray-100 rounded-full">
                                {getEntityTypeLabel(review)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(review.createdAt)}
                              </span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleEditClick(review)}
                              className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Edit review"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(review._id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete review"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>

                        {/* Rating */}
                        <div className="flex items-center gap-1 mb-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              size={16}
                              className={
                                star <= review.rating
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-300'
                              }
                            />
                          ))}
                          <span className="text-sm font-medium text-gray-900 ml-1">
                            {review.rating.toFixed(1)}
                          </span>
                        </div>

                        {/* Review Text */}
                        {review.review_text && (
                          <p className="text-sm text-gray-600 line-clamp-3 mb-3">
                            {review.review_text}
                          </p>
                        )}

                        {/* Footer */}
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <ThumbsUp size={12} />
                            {review.helpful_count} found helpful
                          </span>
                          {review.is_verified_completion && (
                            <span className="flex items-center gap-1 text-green-600">
                              <Award size={12} />
                              Verified Completion
                            </span>
                          )}
                          {review.is_featured && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                              Featured
                            </span>
                          )}
                        </div>

                        {/* Admin Response */}
                        {review.admin_response && (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                            <p className="text-xs font-medium text-blue-900 mb-1">Admin Response:</p>
                            <p className="text-sm text-blue-800">{review.admin_response.text}</p>
                            <p className="text-xs text-blue-600 mt-1">
                              {formatDate(review.admin_response.responded_at)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Edit Review Modal */}
      {reviewToEdit && (
        <ReviewModal
          isOpen={showEditModal}
          onClose={handleEditModalClose}
          targetId={
            reviewToEdit.course_id ||
            reviewToEdit.certification_id ||
            reviewToEdit.learning_path_id ||
            ''
          }
          targetTitle={reviewToEdit.entity_title || 'Review'}
          targetType={reviewToEdit.review_type}
          existingReview={reviewToEdit}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setReviewToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Review"
        message="Are you sure you want to delete this review? This action cannot be undone."
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
